import { useEffect, useRef, useState } from "react";
import {
  getCheckDetails,
} from "../../../assets/nodeApi/postApi/postSchoolApi";
import { postSendOtp, postVerifyOtp, postResendOtp } from "../../../assets/nodeApi/postApi/postCommonApi";

/**
 * useOtpVerification
 *
 * Encapsulates the full OTP send → verify → resend flow including
 * duplicate-mobile checking via getCheckDetails.
 *
 * @param {object}   options
 * @param {"school"|"teacher"|"user"} options.role       - Role passed to getCheckDetails
 * @param {Function} [options.onVerified]                - Called on successful OTP verify
 * @param {Function} [options.onSendSuccess]             - Called after OTP is sent successfully
 * @param {Function} [options.onResendSuccess]           - Called after OTP is resent successfully
 * @param {Function} [options.onError]                   - Called with (message) on any failure
 */
const useOtpVerification = ({
  role,
  onVerified,
  onSendSuccess,
  onResendSuccess,
  onError,
} = {}) => {

    const [showOtpModal, setShowOtpModal] = useState(false);
  const [isVerified,   setIsVerified]   = useState(false);

  const [otp,      setOtp]      = useState("");
  const [otpError, setOtpError] = useState("");

  const [isSendPending,   setIsSendPending]   = useState(false);
  const [isVerifyPending, setIsVerifyPending] = useState(false);
  const [isResendPending, setIsResendPending] = useState(false);

  const [countdown, setCountdown] = useState(0);
  const [canResend,  setCanResend]  = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);


  const formatMobile = (mobile) => `+91${mobile}`;


  const formatMobileForCheck = (mobile) =>
    role === "school" ? `+91${mobile}` : mobile;

  const startCountdown = (duration = 59) => {
    setCountdown(duration);
    setCanResend(false);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };


  const sendOtp = async (mobile, setFieldError) => {
    if (!mobile || !/^[0-9]{10}$/.test(mobile)) {
      setFieldError?.("mobile", { message: "Enter a valid 10-digit mobile number" });
      return false;
    }

    setIsSendPending(true);
    setOtpError("");

    try {
      const checkRes = await getCheckDetails(role, formatMobileForCheck(mobile));
      const mobileResult = checkRes?.results?.find((r) => r.field === "mobile");

      if (mobileResult?.isExist) {
        const msg = "This mobile number is already registered";
        setFieldError?.("mobile", { message: msg });
        onError?.(msg);
        return false;
      }

      // Send OTP
      await postSendOtp({ mobile: formatMobile(mobile) });

      setShowOtpModal(true);
      startCountdown(59);
      setOtpError("");
      onSendSuccess?.();   // e.g. showToaster("Success", "OTP sent!", "success")
      return true;
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to send OTP. Please try again.";
      setFieldError?.("mobile", { message: msg });
      onError?.(msg);
      return false;
    } finally {
      setIsSendPending(false);
    }
  };

  const verifyOtp = async (mobile) => {
    if (!otp || otp.length < 4) {
      setOtpError("Please enter a valid OTP");
      return false;
    }

    setIsVerifyPending(true);
    setOtpError("");

    try {
      await postVerifyOtp({ mobile: formatMobile(mobile), otp });

      setIsVerified(true);
      setShowOtpModal(false);
      setOtp("");
      setOtpError("");
      onVerified?.();  // e.g. clearErrors("mobile") + showToaster
      return true;
    } catch (err) {
      const msg = err?.response?.data?.message || "Invalid OTP. Please try again.";
      setOtpError(msg);
      onError?.(msg);
      return false;
    } finally {
      setIsVerifyPending(false);
    }
  };


  const resendOtp = async (mobile) => {
    if (!canResend) return false;

    setIsResendPending(true);
    setOtpError("");

    try {
      await postResendOtp({ mobile: formatMobile(mobile) });
      startCountdown(59);
      onResendSuccess?.();  // e.g. showToaster("Success", "OTP resent!", "success")
      return true;
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to resend OTP. Please try again.";
      setOtpError(msg);
      onError?.(msg);
      return false;
    } finally {
      setIsResendPending(false);
    }
  };

  const closeModal = () => {
    setShowOtpModal(false);
    setOtp("");
    setOtpError("");
  };

  const resetOtp = () => {
    setIsVerified(false);
    setShowOtpModal(false);
    setOtp("");
    setOtpError("");
    setCountdown(0);
    setCanResend(false);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  return {
    showOtpModal,
    isVerified,
    otp,
    setOtp,
    otpError,
    countdown,
    canResend,

    isSendPending,
    isVerifyPending,
    isResendPending,

    sendOtp,
    verifyOtp,
    resendOtp,
    closeModal,
    resetOtp,
  };
};

export default useOtpVerification;